import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import ProfileMenu from "./ProfileMenu";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";

export default function Header({ onOpenAuth, onOpenSettings }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu when clicking outside
  useEffect(() => {
    if (!mobile) return;
    const onClick = (e) => {
      if (!e.target.closest("header")) setMobile(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [mobile]);

  const goSection = (id) => {
    setMobile(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 60);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const links = [
    { id: "portfolio", label: t.nav.portfolio, tid: NAV.navPortfolio },
    { id: "services", label: t.nav.services, tid: NAV.navServices },
    { id: "process", label: t.nav.process, tid: NAV.navProcess },
    { id: "about", label: t.nav.about, tid: NAV.navAbout },
    { id: "contact", label: t.nav.contact, tid: NAV.navContact },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 glass border-b ${
        scrolled ? "border-[#e5e5e5]" : "border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.navHome}
          className="font-mono font-bold tracking-tighter text-[#0a0a0a] text-base sm:text-lg flex items-center gap-2"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <img src="/kuryarti-icon.png" alt="" className="w-12 h-12 object-contain -translate-x-[7%]" draggable={false} />
          KURYARTI
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => goSection(l.id)}
              data-testid={l.tid}
              className="font-sans font-semibold text-xs uppercase tracking-[0.15em] text-[#0a0a0a] link-mono"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user && user !== false ? (
            <>
              {user.role !== "admin" && (
                <Link
                  to="/nuevo-proyecto"
                  data-testid="nav-new-project-btn"
                  className="hidden md:inline-block font-mono text-xs uppercase tracking-[0.18em] px-4 py-2 bg-[#ff006e] text-white hover:bg-[#0a0a0a] transition-colors"
                >
                  {t.nav.new_project}
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid={NAV.dashboardBtn}
                className="hidden sm:inline-block font-mono text-xs uppercase tracking-[0.18em] px-4 py-2 border border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                {user.role === "admin" ? "Studio" : t.nav.dashboard}
              </Link>
              <ProfileMenu onOpenSettings={onOpenSettings} />
            </>
          ) : (
            <button
              onClick={onOpenAuth}
              data-testid={NAV.loginBtn}
              className="hidden sm:inline-block font-mono text-xs uppercase tracking-[0.18em] px-4 py-2 bg-[#0a0a0a] text-white hover:bg-[#2E1D4F] transition-colors"
            >
              {t.nav.login}
            </button>
          )}
          <button
            onClick={() => setMobile((v) => !v)}
            className="lg:hidden p-2 text-[#0a0a0a]"
            data-testid={NAV.mobileMenu}
            aria-label="Menu"
          >
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden glass border-t border-[#e5e5e5] px-6 py-6 flex flex-col gap-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => goSection(l.id)}
              className="font-mono text-sm uppercase tracking-[0.18em] text-left text-[#0a0a0a] py-1"
            >
              {l.label}
            </button>
          ))}
          {user && user !== false ? (
            <>
              {user.role !== "admin" && (
                <Link
                  to="/nuevo-proyecto"
                  onClick={() => setMobile(false)}
                  className="font-mono text-sm uppercase tracking-[0.18em] px-4 py-3 bg-[#ff006e] text-white text-center"
                >
                  {t.nav.new_project}
                </Link>
              )}
              <Link
                to="/dashboard"
                onClick={() => setMobile(false)}
                className="font-mono text-sm uppercase tracking-[0.18em] px-4 py-3 border border-[#0a0a0a] text-[#0a0a0a] text-center"
              >
                {user.role === "admin" ? "Studio" : t.nav.dashboard}
              </Link>
              <button
                onClick={() => { setMobile(false); logout(); }}
                className="font-mono text-sm uppercase tracking-[0.18em] text-left text-[#0a0a0a] py-1 border-t border-[#e5e5e5] mt-2 pt-3"
              >
                {t.nav.logout}
              </button>
            </>
          ) : (
            <button
              onClick={() => { setMobile(false); onOpenAuth(); }}
              className="font-mono text-sm uppercase tracking-[0.18em] px-4 py-3 bg-[#0a0a0a] text-white"
            >
              {t.nav.login}
            </button>
          )}
        </div>
      )}
    </header>
  );
}

